import {request} from './apiClient';

export type CallerIdStatus = 'pending' | 'verified' | 'failed';

export interface CallerId {
  id: string;
  phoneNumber: string;
  label: string;
  status: CallerIdStatus;
  createdAt: number | null;
}

// Company-scoped outbound caller IDs. All calls hit the backend, which scopes
// everything to the authenticated user's company (tenant isolation) and is the
// only writer of the underlying Firestore collection.
export async function listCallerIds(): Promise<CallerId[]> {
  const payload = await request('/v1/caller-ids', {
    method: 'GET',
    unwrap: false,
    // CallerIdField polls this every 3s while a verification is pending —
    // faster than the client's GET freshness window, which would pin the
    // "pending" status past the actual verification.
    freshForMs: 0,
  });
  return (payload?.callerIds as CallerId[]) || [];
}

export async function verifyCallerId({
  phoneNumber,
  label,
}: {
  phoneNumber: string;
  label: string;
}): Promise<{
  validationCode?: string;
  alreadyVerified?: boolean;
  callerId: CallerId;
}> {
  const payload = await request('/v1/caller-ids/verify', {
    method: 'POST',
    unwrap: false,
    body: JSON.stringify({phoneNumber, label}),
  });
  return {
    validationCode: payload?.validationCode,
    alreadyVerified: Boolean(payload?.alreadyVerified),
    callerId: payload?.callerId as CallerId,
  };
}

export async function deleteCallerId(id: string): Promise<void> {
  await request(`/v1/caller-ids/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    unwrap: false,
  });
}

const callerIdService = {
  listCallerIds,
  verifyCallerId,
  deleteCallerId,
};

export default callerIdService;
